import React from "react";
import { colors } from "./theme";
import { fieldLabelStyle, inputStyle } from "./ui";

// The message text below the label is the actual "this field is wrong"
// signal — WCAG 1.4.1 rules out colour as the only cue, and a red border on
// its own would be exactly that for a colour-blind reader. The border in
// controlStyle() below is a reinforcement of this, never a substitute for it.
const errorTextStyle = {
  fontSize: "var(--fs-meta)",
  lineHeight: 1.5,
  color: colors.danger,
  margin: "6px 0 0",
};

// A label that no longer wraps its control. `<label><input/></label>` is how
// the two forms did this until now, but a custom listbox — replacing the
// native <select> below — is built from div/li, not a form control, and a
// wrapping <label> has nothing to associate with there. htmlFor/id is the one
// association mechanism that works for both, so every field here is wired
// that way already, and swapping the control inside a <Field> later needs no
// change to Field itself. The label also exposes `${id}-label` so a future
// custom control can point aria-labelledby at it directly.
export function Field({ id, label, error, children, style }) {
  return (
    <div style={{ display: "block", ...style }}>
      <label htmlFor={id} id={`${id}-label`} style={{ ...fieldLabelStyle, display: "block" }}>
        {label}
      </label>
      {children}
      {/* Only rendered while there's an error, not hidden with CSS, so a
          screen reader's role="alert" live region fires when the message
          appears — on blur or failed submit — rather than sitting silent in
          the DOM on every keystroke. */}
      {error && (
        <p id={`${id}-error`} role="alert" style={errorTextStyle}>
          {error}
        </p>
      )}
    </div>
  );
}

// The error state has to replace `border` wholesale rather than layer a
// `borderColor` over it. inputStyle sets the shorthand, and React warns — then
// acts on the warning — when a shorthand and its longhand alternate across
// renders for the same property: it drops the longhand and lets the shorthand
// win, so the red border painted correctly on first render and then silently
// reverted to the normal one the next time the field re-rendered. Restating
// the same shorthand keeps the two in the same key slot, so there is nothing
// to reconcile.
function controlStyle(base, error) {
  return error ? { ...base, border: `1px solid ${colors.danger}` } : base;
}

// Forwards its ref to the underlying control so a failed submit can move
// focus to the first invalid field — see focusFirstInvalid() in Contact.jsx
// and ProductEnquiryModal.jsx.
export const TextField = React.forwardRef(function TextField(
  { id, label, error, value, onChange, style, ...rest },
  ref
) {
  return (
    <Field id={id} label={label} error={error} style={style}>
      <input
        ref={ref}
        id={id}
        style={controlStyle(inputStyle, error)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...rest}
      />
    </Field>
  );
});

export const TextArea = React.forwardRef(function TextArea(
  { id, label, error, value, onChange, style, ...rest },
  ref
) {
  return (
    <Field id={id} label={label} error={error} style={style}>
      <textarea
        ref={ref}
        id={id}
        style={controlStyle({ ...inputStyle, resize: "vertical" }, error)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...rest}
      />
    </Field>
  );
});
